(() => {
  const viewKey = 'chicaup-catalog-view';
  let messageTimer;

  const getCatalogRoot = (element) => element.closest('[data-marketplace-catalog]') || document;

  const setCatalogView = (root, view) => {
    const grid = root.querySelector('.product-grid');
    if (!grid) return;

    grid.classList.toggle('catalog-grid--compact', view === 'compact');
    root.querySelectorAll('[data-catalog-view]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.catalogView === view));
    });
  };

  const navigateWithUrl = (updateUrl) => {
    const url = new URL(window.location.href);
    updateUrl(url);
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  };

  const navigateWithParam = (name, value) => {
    navigateWithUrl((url) => url.searchParams.set(name, value));
  };

  const closeToolsPanel = (panel) => {
    if (!panel) return;
    panel.hidden = true;
    const root = getCatalogRoot(panel);
    const toggle = root.querySelector(`[aria-controls="${panel.id}"]`);
    const backdrop = root.querySelector('[data-tools-backdrop]');
    toggle?.setAttribute('aria-expanded', 'false');
    if (backdrop) backdrop.hidden = true;
    document.body.classList.remove('marketplace-tools-open');
  };

  const closeAllToolsPanels = () => {
    document.querySelectorAll('[data-tools-panel]:not([hidden])').forEach(closeToolsPanel);
  };

  const setToolsPanelOpen = (toggle, open) => {
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;
    closeAllToolsPanels();
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    const backdrop = getCatalogRoot(toggle).querySelector('[data-tools-backdrop]');
    if (backdrop) backdrop.hidden = !open;
    document.body.classList.toggle('marketplace-tools-open', open);
    if (open) panel.querySelector('[data-tools-close]')?.focus();
  };

  const showToolMessage = (source, message) => {
    const status = getCatalogRoot(source).querySelector('[data-tool-message]');
    if (!status) return;
    window.clearTimeout(messageTimer);
    status.textContent = message;
    status.hidden = false;
    messageTimer = window.setTimeout(() => {
      status.hidden = true;
    }, 2400);
  };

  const getToolMessage = (source, key) => {
    const toolbar = source.closest('.marketplace-toolbar');
    return toolbar?.dataset[key] || '';
  };

  const copyText = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
  };

  const getCartRenderer = () => document.querySelector('cart-notification') || document.querySelector('cart-drawer');

  const updateHeaderCartCount = (itemCount) => {
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (!cartIcon || !Number.isFinite(itemCount)) return;

    let bubble = cartIcon.querySelector('.cart-count-bubble');
    if (itemCount <= 0) {
      bubble?.remove();
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
      cartIcon.appendChild(bubble);
    }

    const visibleCount = bubble.querySelector('[aria-hidden="true"]');
    const accessibleCount = bubble.querySelector('.visually-hidden');
    if (visibleCount) visibleCount.textContent = itemCount < 100 ? String(itemCount) : '99+';
    if (accessibleCount) accessibleCount.textContent = `${itemCount} items in cart`;
  };

  const syncHeaderCart = (payload) => {
    const cartIcon = document.getElementById('cart-icon-bubble');
    const iconBubbleHtml = payload?.sections?.['cart-icon-bubble'];

    if (cartIcon && iconBubbleHtml) {
      const parsedIconBubble = new DOMParser()
        .parseFromString(iconBubbleHtml, 'text/html')
        .querySelector('#cart-icon-bubble');
      if (parsedIconBubble) cartIcon.innerHTML = parsedIconBubble.innerHTML;
    }

    if (Number.isFinite(Number(payload?.item_count))) {
      updateHeaderCartCount(Number(payload.item_count));
    }
  };

  const getInlineVariantId = (form) => Number(form.querySelector('[name="id"]')?.value);

  const getInlineCartState = async (form) => {
    const variantId = getInlineVariantId(form);
    if (!variantId) return { cart: null, item: null };
    const response = await fetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Unable to load cart');
    const cart = await response.json();
    const item = cart.items.find((cartItem) => cartItem.variant_id === variantId) || null;
    return { cart, item };
  };

  const getInlineCartItem = async (form) => (await getInlineCartState(form)).item;

  const refreshInlineQuantity = async (form, fallbackQuantity = 1) => {
    const variantId = getInlineVariantId(form);
    const quantityNode = form.querySelector('[data-inline-quantity]');
    if (!variantId || !quantityNode) return fallbackQuantity;

    try {
      const item = await getInlineCartItem(form);
      const quantity = item?.quantity || fallbackQuantity;
      quantityNode.textContent = String(quantity);
      if (item?.key) form.dataset.inlineCartKey = item.key;
      return quantity;
    } catch (error) {
      quantityNode.textContent = String(fallbackQuantity);
      return fallbackQuantity;
    }
  };

  const setInlineBusy = (form, busy) => {
    form.classList.toggle('is-loading', busy);
    form.querySelectorAll('button').forEach((button) => {
      if (button.disabled && !button.dataset.inlineWasEnabled) return;
      if (busy) {
        button.dataset.inlineWasEnabled = 'true';
        button.disabled = true;
      } else if (button.dataset.inlineWasEnabled) {
        button.disabled = false;
        delete button.dataset.inlineWasEnabled;
      }
    });
  };

  const renderCartContents = (payload, activeElement, openNotification = true) => {
    const cart = getCartRenderer();
    syncHeaderCart(payload);
    if (!cart) return;
    if (activeElement && 'setActiveElement' in cart) cart.setActiveElement(activeElement);

    if (cart.tagName === 'CART-NOTIFICATION' && !payload.key) {
      return;
    }

    if ('renderContents' in cart && openNotification) cart.renderContents(payload);
    if (cart.classList?.contains('is-empty')) cart.classList.remove('is-empty');
  };

  const addInlineCartItem = async (form) => {
    const cart = getCartRenderer();
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(form);
    if (cart) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
    }
    config.body = formData;

    const response = await fetch(`${routes.cart_add_url}`, config);
    const payload = await response.json();
    if (payload.status) throw new Error(payload.description || payload.message);
    if (payload.key) form.dataset.inlineCartKey = payload.key;
    renderCartContents(payload, document.activeElement, true);
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'marketplace-inline-cart',
      productVariantId: formData.get('id'),
      cartData: payload,
    });
    return refreshInlineQuantity(form, Number(formData.get('quantity')) || 1);
  };

  const changeInlineCartItem = async (form, operation) => {
    const cart = getCartRenderer();
    const variantId = String(getInlineVariantId(form) || '');
    if (!variantId) return 0;
    const quantityNode = form.querySelector('[data-inline-quantity]');
    const currentQuantity = Number(quantityNode?.textContent) || 0;
    const quantity = operation === 'remove' ? 0 : Math.max(0, currentQuantity - 1);
    const lineItemKey = form.dataset.inlineCartKey;
    const buildConfig = (body) => {
      const config = fetchConfig('javascript');
      config.headers['Content-Type'] = 'application/json';
      config.body = JSON.stringify(body);
      return config;
    };
    const sections = cart ? cart.getSectionsToRender().map((section) => section.id) : [];
    const changeBody = {
      id: lineItemKey,
      quantity,
      sections,
      sections_url: window.location.pathname,
    };

    let response;
    let payload;
    let updatedItem;
    if (lineItemKey) {
      response = await fetch(`${routes.cart_change_url}`, buildConfig(changeBody));
      payload = await response.json();
      updatedItem = payload.items?.find((lineItem) => String(lineItem.variant_id) === variantId);
    }
    if (!lineItemKey || !response.ok || payload.status || (updatedItem?.quantity || 0) !== quantity) {
      response = await fetch(
        `${routes.cart_update_url}`,
        buildConfig({
          updates: { [variantId]: quantity },
          sections,
          sections_url: window.location.pathname,
        })
      );
      payload = await response.json();
      updatedItem = payload.items?.find((lineItem) => String(lineItem.variant_id) === variantId);
    }
    if (!response.ok) throw new Error('Unable to update cart');
    if (payload.status) throw new Error(payload.description || payload.message);
    renderCartContents(payload, document.activeElement, false);
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'marketplace-inline-cart',
      productVariantId: variantId,
      cartData: payload,
    });
    const item = updatedItem;
    if (quantityNode) quantityNode.textContent = String(item?.quantity || 0);
    if (item?.key) {
      form.dataset.inlineCartKey = item.key;
    } else {
      delete form.dataset.inlineCartKey;
    }
    return item?.quantity || 0;
  };

  const setInlineStepperVisible = (form, visible) => {
    const addButton = form.querySelector('.marketplace-card-inline-add__button');
    const stepper = form.querySelector('[data-inline-stepper]');
    if (!addButton || !stepper) return;
    addButton.hidden = visible;
    stepper.hidden = !visible;
    form.classList.toggle('has-inline-quantity', visible);
  };

  const initProductCarousel = (carousel) => {
    if (carousel.dataset.carouselReady === 'true') return;

    const viewport = carousel.querySelector('[data-carousel-viewport]');
    const track = carousel.querySelector('.marketplace-product-carousel__track');
    const previousButton = carousel.querySelector('[data-carousel-previous]');
    const nextButton = carousel.querySelector('[data-carousel-next]');
    const toggleButton = carousel.querySelector('[data-carousel-toggle]');
    if (!viewport || !track || !previousButton || !nextButton || !toggleButton) return;

    carousel.dataset.carouselReady = 'true';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const speed = Number(carousel.dataset.autoplaySpeed) || 70;
    let frame;
    let lastTime = 0;
    let userPaused = reduceMotion;

    const cardStep = () => {
      const item = track.querySelector('[data-carousel-item]');
      if (!item) return viewport.clientWidth;
      const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
      return item.getBoundingClientRect().width + gap;
    };

    const loopWidth = () => Math.max(0, track.scrollWidth / 2);

    const move = (direction) => {
      const step = cardStep();
      const maximum = Math.max(0, loopWidth() - viewport.clientWidth);
      let nextPosition = viewport.scrollLeft + step * direction;
      if (direction > 0 && nextPosition > maximum - 2) nextPosition = 0;
      if (direction < 0 && nextPosition < 2) nextPosition = maximum;
      viewport.scrollTo({ left: nextPosition, behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    const tick = (time) => {
      if (!lastTime) lastTime = time;
      const elapsed = time - lastTime;
      lastTime = time;

      if (!userPaused && !document.hidden) {
        const width = loopWidth();
        viewport.scrollLeft += (elapsed / 1000) * speed;
        if (width > 0 && viewport.scrollLeft >= width) {
          viewport.scrollLeft -= width;
        }
      }
      frame = window.requestAnimationFrame(tick);
    };

    const schedule = () => {
      window.cancelAnimationFrame(frame);
      lastTime = 0;
      frame = window.requestAnimationFrame(tick);
    };

    const setUserPaused = (paused) => {
      userPaused = paused;
      toggleButton.setAttribute('aria-pressed', String(paused));
      toggleButton.setAttribute('aria-label', paused ? toggleButton.dataset.labelPlay : toggleButton.dataset.labelPause);
      schedule();
    };

    previousButton.addEventListener('click', () => {
      move(-1);
      schedule();
    });
    nextButton.addEventListener('click', () => {
      move(1);
      schedule();
    });
    toggleButton.addEventListener('click', () => setUserPaused(!userPaused));
    document.addEventListener('visibilitychange', schedule);
    setUserPaused(userPaused);
  };

  const initFloatingGuide = (guide) => {
    if (guide.dataset.guideReady === 'true') return;

    const trigger = guide.querySelector('[data-guide-trigger]');
    const content = guide.querySelector('[data-guide-content]');
    const topButton = guide.querySelector('[data-guide-top]');
    if (!trigger || !content || !topButton) return;

    guide.dataset.guideReady = 'true';
    let pointerInside = false;
    let focusInside = false;

    const setExpanded = (expanded) => {
      guide.classList.toggle('is-expanded', expanded);
      guide.classList.toggle('is-collapsed', !expanded);
      trigger.setAttribute('aria-expanded', String(expanded));
      content.hidden = false;
    };

    setExpanded(false);

    guide.addEventListener('pointerenter', () => {
      pointerInside = true;
      setExpanded(true);
    });
    guide.addEventListener('pointerleave', () => {
      pointerInside = false;
      if (!focusInside) setExpanded(false);
    });
    guide.addEventListener('focusin', () => {
      focusInside = true;
      setExpanded(true);
    });
    guide.addEventListener('focusout', (event) => {
      if (guide.contains(event.relatedTarget)) return;
      focusInside = false;
      if (!pointerInside) setExpanded(false);
    });

    trigger.addEventListener('click', () => {
      setExpanded(!guide.classList.contains('is-expanded'));
    });

    topButton.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    });
  };

  const initMembershipPopup = (popup) => {
    if (popup.dataset.popupReady === 'true') return;
    popup.dataset.popupReady = 'true';

    const dialog = popup.querySelector('[data-membership-dialog]');
    const closeButtons = popup.querySelectorAll('[data-membership-close]');
    const visitorNumber = popup.querySelector('[data-visitor-number]');
    if (!dialog || !closeButtons.length) return;

    const storageKey = 'chicaup-membership-popup-seen';
    const showEveryVisit = popup.dataset.showEveryVisit === 'true';
    const frequencyDays = Number(popup.dataset.frequencyDays) || 7;
    const delay = Number(popup.dataset.popupDelay) || 1400;
    const numberEpoch = Date.UTC(2026, 0, 1);
    const welcomeNumber = 12000 + Math.max(0, Math.floor((Date.now() - numberEpoch) / 600000));
    let previouslyFocused;

    if (visitorNumber) visitorNumber.textContent = `#${welcomeNumber.toLocaleString('en-US')}`;

    const closePopup = () => {
      if (popup.hidden) return;
      popup.classList.remove('is-open');
      document.body.classList.remove('membership-popup-open');
      window.setTimeout(() => {
        popup.hidden = true;
        previouslyFocused?.focus?.();
      }, 220);
    };

    const openPopup = () => {
      previouslyFocused = document.activeElement;
      popup.hidden = false;
      document.body.classList.add('membership-popup-open');
      window.requestAnimationFrame(() => popup.classList.add('is-open'));
      window.setTimeout(() => popup.querySelector('[data-membership-close-button]')?.focus(), 230);
      if (!showEveryVisit) {
        try {
          window.localStorage.setItem(storageKey, String(Date.now()));
        } catch (error) {
          // The popup still works when browser storage is unavailable.
        }
      }
    };

    closeButtons.forEach((button) => button.addEventListener('click', closePopup));
    popup.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closePopup();
      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    let lastSeen = 0;
    try {
      lastSeen = Number(window.localStorage.getItem(storageKey)) || 0;
    } catch (error) {
      lastSeen = 0;
    }
    const shouldShow = showEveryVisit || Date.now() - lastSeen >= frequencyDays * 24 * 60 * 60 * 1000;
    if (shouldShow || Shopify.designMode) window.setTimeout(openPopup, Shopify.designMode ? 200 : delay);
  };

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target instanceof Element ? event.target.closest('[data-marketplace-inline-form]') : null;
      if (!form) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (form.classList.contains('is-loading')) return;

      try {
        setInlineBusy(form, true);
        const quantity = await addInlineCartItem(form);
        form.querySelector('[data-inline-quantity]').textContent = String(quantity);
        setInlineStepperVisible(form, true);
      } catch (error) {
        console.error(error);
      } finally {
        setInlineBusy(form, false);
      }
    },
    true
  );

  document.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const inlineForm = target.closest('[data-marketplace-inline-form]');
    if (inlineForm) {
      const quantityNode = inlineForm.querySelector('[data-inline-quantity]');
      const currentQuantity = Number(quantityNode?.textContent) || 0;
      let action = null;

      if (target.closest('[data-inline-add], [data-inline-increase]')) {
        action = 'add';
      } else if (target.closest('[data-inline-decrease]')) {
        action = 'decrease';
      } else if (target.closest('[data-inline-remove]')) {
        action = 'remove';
      }

      if (action) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (inlineForm.classList.contains('is-loading')) return;
        try {
          setInlineBusy(inlineForm, true);
          const quantity = action === 'add' ? await addInlineCartItem(inlineForm) : await changeInlineCartItem(inlineForm, action);
          const quantityNode = inlineForm.querySelector('[data-inline-quantity]');
          if (quantityNode) quantityNode.textContent = String(quantity);
          setInlineStepperVisible(inlineForm, quantity > 0);
        } catch (error) {
          console.error(error);
        } finally {
          setInlineBusy(inlineForm, false);
        }
        return;
      }
    }

    const toolsToggle = target.closest('[data-tools-toggle]');
    if (toolsToggle) {
      const shouldOpen = toolsToggle.getAttribute('aria-expanded') !== 'true';
      setToolsPanelOpen(toolsToggle, shouldOpen);
      return;
    }

    const toolsClose = target.closest('[data-tools-close], [data-tools-backdrop]');
    if (toolsClose) {
      closeAllToolsPanels();
      return;
    }

    const openPanel = document.querySelector('[data-tools-panel]:not([hidden])');
    if (openPanel && !target.closest('[data-tools-panel]')) closeToolsPanel(openPanel);

    const viewButton = target.closest('[data-catalog-view]');
    if (viewButton) {
      const root = getCatalogRoot(viewButton);
      const view = viewButton.dataset.catalogView;
      setCatalogView(root, view);
      try {
        window.localStorage.setItem(viewKey, view);
      } catch (error) {
        // The visual control still works when browser storage is unavailable.
      }
      return;
    }

    const sortButton = target.closest('[data-catalog-sort]');
    if (sortButton) {
      navigateWithParam('sort_by', sortButton.dataset.catalogSort);
      return;
    }

    const stockButton = target.closest('[data-catalog-stock]');
    if (stockButton) {
      navigateWithUrl((url) => {
        const isActive = url.searchParams.get('filter.v.availability') === '1';
        if (isActive) {
          url.searchParams.delete('filter.v.availability');
        } else {
          url.searchParams.set('filter.v.availability', '1');
        }
      });
      return;
    }

    const availabilityButton = target.closest('[data-catalog-availability]');
    if (availabilityButton) {
      navigateWithParam('filter.v.availability', availabilityButton.dataset.catalogAvailability);
      return;
    }

    const priceButton = target.closest('[data-price-min], [data-price-max]');
    if (priceButton) {
      navigateWithUrl((url) => {
        url.searchParams.delete('filter.v.price.gte');
        url.searchParams.delete('filter.v.price.lte');
        if (priceButton.dataset.priceMin) {
          url.searchParams.set('filter.v.price.gte', priceButton.dataset.priceMin);
        }
        if (priceButton.dataset.priceMax) {
          url.searchParams.set('filter.v.price.lte', priceButton.dataset.priceMax);
        }
      });
      return;
    }

    const clearButton = target.closest('[data-clear-catalog-filters]');
    if (clearButton) {
      navigateWithUrl((url) => {
        Array.from(url.searchParams.keys()).forEach((key) => {
          if (key.startsWith('filter.')) url.searchParams.delete(key);
        });
        url.searchParams.delete('sort_by');
      });
      return;
    }

    const copyButton = target.closest('[data-copy-catalog-link]');
    if (copyButton) {
      try {
        await copyText(window.location.href);
        showToolMessage(copyButton, getToolMessage(copyButton, 'messageLinkCopied'));
      } catch (error) {
        showToolMessage(copyButton, getToolMessage(copyButton, 'messageCopyFailed'));
      }
      return;
    }

    const shareButton = target.closest('[data-share-catalog]');
    if (shareButton) {
      try {
        if (navigator.share) {
          await navigator.share({ title: document.title, url: window.location.href });
          showToolMessage(shareButton, getToolMessage(shareButton, 'messageShared'));
        } else {
          await copyText(window.location.href);
          showToolMessage(shareButton, getToolMessage(shareButton, 'messageLinkCopied'));
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          showToolMessage(shareButton, getToolMessage(shareButton, 'messageShareFailed'));
        }
      }
      return;
    }

    const printButton = target.closest('[data-print-catalog]');
    if (printButton) {
      closeAllToolsPanels();
      window.print();
      return;
    }

    const sidebarButton = target.closest('[data-sidebar-toggle]');
    if (sidebarButton) {
      const sidebar = document.getElementById(sidebarButton.getAttribute('aria-controls'));
      if (!sidebar) return;
      const isOpen = sidebar.classList.toggle('is-open');
      sidebarButton.setAttribute('aria-expanded', String(isOpen));
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAllToolsPanels();
  });

  document.addEventListener('DOMContentLoaded', () => {
    let savedView = 'comfortable';
    try {
      savedView = window.localStorage.getItem(viewKey) || savedView;
    } catch (error) {
      savedView = 'comfortable';
    }

    document.querySelectorAll('[data-marketplace-catalog]').forEach((root) => setCatalogView(root, savedView));

    const url = new URL(window.location.href);
    const availability = url.searchParams.get('filter.v.availability');
    const priceMin = url.searchParams.get('filter.v.price.gte') || '';
    const priceMax = url.searchParams.get('filter.v.price.lte') || '';

    document.querySelectorAll('[data-catalog-stock]').forEach((button) => {
      button.setAttribute('aria-pressed', String(availability === '1'));
    });
    document.querySelectorAll('[data-catalog-availability]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.catalogAvailability === availability));
    });
    document.querySelectorAll('[data-price-min], [data-price-max]').forEach((button) => {
      const isActive = (button.dataset.priceMin || '') === priceMin && (button.dataset.priceMax || '') === priceMax;
      button.setAttribute('aria-pressed', String(isActive));
    });

    document.querySelectorAll('[data-product-carousel]').forEach(initProductCarousel);
    document.querySelectorAll('[data-floating-guide]').forEach(initFloatingGuide);
    document.querySelectorAll('[data-membership-popup]').forEach(initMembershipPopup);
  });

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-product-carousel]').forEach(initProductCarousel);
    event.target.querySelectorAll('[data-floating-guide]').forEach(initFloatingGuide);
    event.target.querySelectorAll('[data-membership-popup]').forEach(initMembershipPopup);
  });
})();
